import type { ReactNode } from "react";

import { adminUiCopy } from "@/copy/adminUiCopy";
import { coercePublicErrorResponse } from "@/utils/errors/publicErrorContract";
import { VNextButton, VNextNotice } from "@/vnext/primitives";

export const DEFAULT_ADMIN_LOAD_FAILURE_HELP =
  adminUiCopy.adminLoadFailure.defaultHelp;

interface AdminLoadFailureNoticeProps {
  title: string;
  message: string;
  helpText?: string;
  error?: unknown;
  onRetry?: (() => void) | undefined;
  retryLabel?: string;
  action?: ReactNode;
}

type ErrorRecord = Record<string, unknown>;
const isErrorRecord = (value: unknown): value is ErrorRecord =>
  Boolean(value) && typeof value === "object";

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toPublicErrorMetadata = (
  error: unknown,
): { errorCode: string | null; correlationId: string | null } => {
  if (!isErrorRecord(error)) {
    return { errorCode: null, correlationId: null };
  }

  const data = isErrorRecord(error.data) ? error.data : (error.data ?? error);
  const status = typeof error.status === "number" ? error.status : undefined;
  const correlationId =
    normalizeString(error.correlationId) ??
    (isErrorRecord(data) ? normalizeString(data.correlationId) : null);
  const publicError = coercePublicErrorResponse({
    status,
    data,
    correlationId,
  });
  const normalizedCorrelationId = normalizeString(publicError.correlationId);

  return {
    errorCode: normalizeString(publicError.errorCode),
    correlationId:
      normalizedCorrelationId && normalizedCorrelationId !== "unknown"
        ? normalizedCorrelationId
        : null,
  };
};

/**
 * Keeps retry/help guidance consistent on high-traffic admin load failures.
 */
export default function AdminLoadFailureNotice({
  title,
  message,
  helpText = DEFAULT_ADMIN_LOAD_FAILURE_HELP,
  error,
  onRetry,
  retryLabel = adminUiCopy.common.retry,
  action,
}: AdminLoadFailureNoticeProps) {
  const metadata = toPublicErrorMetadata(error);
  const resolvedAction =
    (action ?? onRetry) ? (
      <div className="vnext-inline-actions">
        {action}
        {onRetry ? (
          <VNextButton onClick={onRetry} tone="ghost">
            {retryLabel}
          </VNextButton>
        ) : null}
      </div>
    ) : undefined;

  return (
    <VNextNotice action={resolvedAction} tone="danger">
      <strong>{title}</strong>
      <div>{message}</div>
      <div>{helpText}</div>
      {metadata.errorCode ? (
        <div>
          {adminUiCopy.common.errorCodeLabel}: <code>{metadata.errorCode}</code>
        </div>
      ) : null}
      {metadata.correlationId ? (
        <div>
          {adminUiCopy.common.correlationIdLabel}:{" "}
          <code>{metadata.correlationId}</code>
        </div>
      ) : null}
    </VNextNotice>
  );
}
